/**
 * Preview visual del banner de un COMBO. Layout configurable desde
 * `/admin/combo-banner-layout` (panel de controles, sin drag&drop — ver
 * `AdminComboBannerLayoutPage.js`). Los colores (fondo/nombre/precio/acento/
 * footer) caen por defecto a `storeSettings.bannerLayout` (el editor de
 * productos) si el admin todavía no configuró nada específico para combos,
 * para no romper la apariencia de banners ya generados. Los tamaños de
 * fuente y toggles (precio unitario tachado, badge de descuento, "AHORRÁS",
 * total tachado) son propios de `storeSettings.comboBannerLayout`.
 * Se usa tanto en el overlay del catálogo (click → ver/descargar) como en
 * `ComboBannerRenderPage` (captura server-side vía Playwright).
 */
import React from 'react';

const FMT_RATIO = {
  square: { w: 1, h: 1 },
  'ig-story': { w: 9, h: 16 },
  'wa-square': { w: 1, h: 1 },
  'wa-horizontal': { w: 1200, h: 630 },
};

// Defaults propios de combo (independientes del editor de productos).
// Exportado para que el editor admin arranque con los mismos valores que
// ve el preview cuando `comboBannerLayout` todavía está vacío.
export const COMBO_LAYOUT_DEFAULTS = {
  namePx: 34, nameBold: true, nameUppercase: true,
  itemNamePx: 20, itemPricePx: 21, itemOriginalPx: 16,
  showUnitPriceStrikethrough: true,
  showDiscountBadge: true, discountBadgePx: 18,
  showSavingsBadge: true, savingsPx: 20,
  showTotalStrikethrough: true, totalOriginalPx: 22, totalPricePx: 50,
  fontFamily: '',
};

export function ComboBannerPreview({ combo, storeSettings, format = 'ig-story', previewW = 540 }) {
  const bl = storeSettings?.bannerLayout || {};
  const cl = storeSettings?.comboBannerLayout || {};
  const bgColor = cl.bgColor || bl.bgColor || '#ffffff';
  const nameColor = cl.nameColor || bl.nameColor || '#EC4899';
  const priceColor = cl.priceColor || bl.priceColor || '#06B6D4';
  const accentColor = cl.accentColor || bl.accentColor || '#FB923C';
  const footerText = (cl.footerText || bl.footerText || 'Pedilo por WhatsApp').replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').trim();
  const footerColor = cl.footerTextColor || bl.footerTextColor || '#404040';
  const footerBg = cl.footerBgColor || bl.footerBgColor;
  const storePhone = storeSettings?.storePhone || '3571 52-2400';
  const borderWidth = cl.borderWidth ?? bl.borderWidth ?? 0;
  const borderColor = cl.borderColor || bl.borderColor || '#EC4899';

  // Tamaños de texto + toggles propios de combo (con defaults más grandes/
  // legibles que la versión anterior).
  const namePx = cl.namePx ?? COMBO_LAYOUT_DEFAULTS.namePx;
  const nameBold = cl.nameBold !== false;
  const nameUppercase = cl.nameUppercase !== false;
  const itemNamePx = cl.itemNamePx ?? COMBO_LAYOUT_DEFAULTS.itemNamePx;
  const itemPricePx = cl.itemPricePx ?? COMBO_LAYOUT_DEFAULTS.itemPricePx;
  const itemOriginalPx = cl.itemOriginalPx ?? COMBO_LAYOUT_DEFAULTS.itemOriginalPx;
  const showUnitStrike = cl.showUnitPriceStrikethrough !== false;
  const showDiscountBadge = cl.showDiscountBadge !== false;
  const discountBadgePx = cl.discountBadgePx ?? COMBO_LAYOUT_DEFAULTS.discountBadgePx;
  const showSavingsBadge = cl.showSavingsBadge !== false;
  const savingsPx = cl.savingsPx ?? COMBO_LAYOUT_DEFAULTS.savingsPx;
  const showTotalStrike = cl.showTotalStrikethrough !== false;
  const totalOriginalPx = cl.totalOriginalPx ?? COMBO_LAYOUT_DEFAULTS.totalOriginalPx;
  const totalPricePx = cl.totalPricePx ?? COMBO_LAYOUT_DEFAULTS.totalPricePx;
  const fontFamily = cl.fontFamily || '';
  const cssFont = fontFamily === 'inter' ? 'Inter, system-ui, sans-serif' : '"Barlow Condensed", Anton, sans-serif';

  const items = combo?.products || [];
  const pricingMode = combo?.pricingMode || 'discount';
  const originalTotal = items.reduce((sum, cp) => sum + (Number(cp.originalPrice) || 0) * (Number(cp.quantity) || 1), 0);
  let comboPrice;
  let discountPct;
  if (pricingMode === 'manual') {
    comboPrice = items.reduce((sum, cp) => sum + (Number(cp.comboUnitPrice ?? cp.originalPrice) || 0) * (Number(cp.quantity) || 1), 0);
    discountPct = originalTotal > 0 ? Math.round((1 - comboPrice / originalTotal) * 1000) / 10 : 0;
  } else {
    discountPct = Number(combo?.discount) || 0;
    comboPrice = originalTotal > 0 ? Math.round(originalTotal * (1 - discountPct / 100)) : (Number(combo?.comboPrice) || 0);
  }
  const savings = originalTotal - comboPrice;
  const factor = originalTotal > 0 ? comboPrice / originalTotal : 1;
  const fmt = (n) => `$${Math.round(n || 0).toLocaleString('es-AR')}`;

  const rObj = FMT_RATIO[format] || FMT_RATIO['ig-story'];
  const ratio = `${rObj.w} / ${rObj.h}`;
  const MAX_WIDTH = format === 'ig-story' ? 540 : format === 'wa-horizontal' ? 760 : 720;
  const fontScale = previewW / 540;
  const sp = (px) => `${(px || 0) * fontScale}px`;

  return (
    <div className="relative mx-auto overflow-hidden"
      style={{
        width: '100%', maxWidth: MAX_WIDTH, aspectRatio: ratio,
        background: bgColor,
        border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : undefined,
        borderRadius: '24px',
        fontFamily: cssFont,
        display: 'flex', flexDirection: 'column',
      }}>
      <div style={{ padding: sp(28), flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Logo */}
        <img src="/bebida-bacana-logo.png" alt="logo" style={{ width: sp(130), height: 'auto', display: 'block', marginBottom: sp(18) }} />

        {/* Badges COMBO + descuento */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: sp(18) }}>
          <span style={{
            background: accentColor, color: '#fff', fontWeight: 900, textTransform: 'uppercase',
            fontSize: sp(15), padding: `${sp(6)} ${sp(14)}`, borderRadius: 999, letterSpacing: 1,
          }}>Combo</span>
          {showDiscountBadge && discountPct > 0 && (
            <span style={{
              background: '#FDE047', color: '#DC2626', fontWeight: 900,
              fontSize: sp(discountBadgePx), padding: `${sp(6)} ${sp(14)}`, borderRadius: 999,
            }}>-{Math.round(discountPct)}% OFF</span>
          )}
        </div>

        {/* Nombre */}
        <p style={{
          fontWeight: nameBold ? 900 : 600, textTransform: nameUppercase ? 'uppercase' : 'none', color: nameColor,
          fontSize: sp(namePx), lineHeight: 1.1, margin: 0, marginBottom: sp(16),
          display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden',
        }}>{combo?.name || 'Combo'}</p>

        <div style={{ borderTop: '2px solid #E4E4E7', marginBottom: sp(14) }} />

        {/* Lista de productos incluidos — precio UNITARIO original tachado
            + unitario con descuento de combo (pedido explícito del cliente:
            antes solo se veía un número, sin dejar claro si tenía descuento). */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: sp(12), overflow: 'hidden', flex: 1 }}>
          {items.slice(0, 10).map((cp, i) => {
            const isBonif = !!cp.bonificado || cp.comboUnitPrice === 0;
            const originalUnit = Number(cp.originalPrice) || 0;
            const finalUnit = pricingMode === 'manual'
              ? Number(cp.comboUnitPrice ?? cp.originalPrice) || 0
              : Math.round(originalUnit * factor);
            const hasLineDiscount = showUnitStrike && !isBonif && finalUnit < originalUnit;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: sp(10), fontFamily: 'Inter, system-ui, sans-serif' }}>
                {cp.imageUrl && (
                  <img src={encodeURI(cp.imageUrl)} alt="" style={{ width: sp(34), height: sp(34), objectFit: 'contain', flexShrink: 0 }}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                <span style={{ flex: 1, fontSize: sp(itemNamePx), fontWeight: 700, color: '#3F3F46', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {cp.quantity}x {cp.name}
                </span>
                {isBonif ? (
                  <span style={{ fontSize: sp(itemPricePx), fontWeight: 800, color: '#059669' }}>BONIF</span>
                ) : hasLineDiscount ? (
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: sp(6), flexShrink: 0 }}>
                    <span style={{ fontSize: sp(itemOriginalPx), color: '#A1A1AA', textDecoration: 'line-through' }}>{fmt(originalUnit)}</span>
                    <span style={{ fontSize: sp(itemPricePx), fontWeight: 800, color: priceColor }}>{fmt(finalUnit)}</span>
                  </span>
                ) : (
                  <span style={{ fontSize: sp(itemPricePx), fontWeight: 800, color: priceColor, flexShrink: 0 }}>{fmt(finalUnit)}</span>
                )}
              </div>
            );
          })}
          {items.length > 10 && (
            <span style={{ fontSize: sp(14), color: '#71717A', fontFamily: 'Inter, system-ui, sans-serif' }}>+ {items.length - 10} producto(s) más</span>
          )}
        </div>

        {/* Ahorro */}
        {showSavingsBadge && savings > 0 && (
          <div style={{
            marginTop: sp(14), background: '#D1FAE5', color: '#047857', fontWeight: 800,
            fontSize: sp(savingsPx), padding: `${sp(8)} ${sp(14)}`, borderRadius: 12, display: 'inline-block',
            fontFamily: 'Inter, system-ui, sans-serif', alignSelf: 'flex-start',
          }}>AHORRÁS {fmt(savings)}</div>
        )}

        {/* Precio */}
        <div style={{ marginTop: sp(18) }}>
          {showTotalStrike && savings > 0 && (
            <span style={{ display: 'block', fontSize: sp(totalOriginalPx), color: '#A1A1AA', textDecoration: 'line-through', fontFamily: 'Inter, system-ui, sans-serif' }}>
              {fmt(originalTotal)}
            </span>
          )}
          <span style={{ display: 'block', fontWeight: 900, fontSize: sp(totalPricePx), color: priceColor, lineHeight: 1 }}>
            {fmt(comboPrice)}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        borderTop: '2px dashed #D4D4D8', padding: `${sp(10)} ${sp(28)} ${sp(14)}`,
        background: footerBg && !['#ffffff', '#fff', 'transparent'].includes((footerBg || '').toLowerCase()) ? footerBg : undefined,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <span style={{ fontSize: sp(14), fontWeight: 700, color: footerColor }}>{footerText}</span>
        <span style={{ fontSize: sp(15), fontWeight: 900, color: footerColor }}>{storePhone}</span>
      </div>
    </div>
  );
}
